import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../contexts/ToastContext";
import employeeService from "../../services/employeeService";
import leaveService from "../../services/leaveService";
import apiClient from "../../services/api_client";
import { getErrorMessage } from "../../utils/errorHandler";
import PageHeader from "../../components/ui/PageHeader";
import FilterBar from "../../components/ui/FilterBar";
import { Plus, Pencil, Trash2 } from "lucide-react";
import "../../styles/dashboard.css";

const numberValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      const number = Number(value);
      if (Number.isFinite(number)) return number;
    }
  }
  return 0;
};

const reportN1Remaining = (row) =>
  numberValue(
    row.n1_remaining,
    row.previous_year_remaining,
    row.annual_previous_remaining,
    row.remaining_n1,
    row.annual_balance_n1
  );

const reportNRemaining = (row) => {
  const explicit = row.n_remaining ?? row.current_year_remaining ?? row.remaining_n ?? row.annual_balance_n;
  if (explicit !== undefined && explicit !== null && explicit !== "") {
    return numberValue(explicit);
  }
  // Le rapport peut renvoyer annual_remaining comme total N-1 + N.
  if (row.annual_remaining !== undefined && row.annual_remaining !== null && row.annual_remaining !== "") {
    return Math.max(0, numberValue(row.annual_remaining) - reportN1Remaining(row));
  }
  return 0;
};

const reportAnnualTotal = (row) => reportN1Remaining(row) + reportNRemaining(row);
const getRecoveryRemaining = (row) => {
  const detail = (row.balances_by_type || []).find((item) =>
    ["RECUPERATION", "RECUP", "REPOS"].includes(
      String(item.code || "").trim().toUpperCase()
    )
  );

  // Priorité au solde restant calculé par le backend.
  if (
    row.recovery_remaining !== undefined &&
    row.recovery_remaining !== null
  ) {
    return Math.max(0, Number(row.recovery_remaining) || 0);
  }

  // Compatibilité avec les détails par type de congé.
  if (detail?.remaining !== undefined && detail?.remaining !== null) {
    return Math.max(0, Number(detail.remaining) || 0);
  }

  return null;
};


const EmployeesManagement = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [employees, setEmployees] = useState([]);
  // Source de vérité du solde annuel : le même rapport que la page Rapport annuel.
  const [annualTotalsByEmployee, setAnnualTotalsByEmployee] = useState({});
  const [recoveryTotalsByEmployee, setRecoveryTotalsByEmployee] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("ALL");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  // Draft rest balances keyed by leave_balance.id
  const [restDraft, setRestDraft] = useState({});

  const departmentsList = useMemo(() => {
    const depts = new Set();
    employees.forEach(e => { if (e.department) depts.add(e.department); });
    return Array.from(depts);
  }, [employees]);

  const load = async () => {
    setError("");
    setLoading(true);

    try {
      console.log("[EmployeesManagement] Chargement des collaborateurs...");

      const list = await Promise.race([
        employeeService.getAll(),
        new Promise((_, reject) => {
          setTimeout(
            () => reject(
              new Error(
                "Délai dépassé lors du chargement des collaborateurs. Vérifiez le backend."
              )
            ),
            15000
          );
        }),
      ]);

      console.log("[EmployeesManagement] Réponse GET /employees :", list);

      const employeeList = Array.isArray(list)
        ? list
        : Array.isArray(list?.items)
        ? list.items
        : Array.isArray(list?.employees)
        ? list.employees
        : null;

      if (!employeeList) {
        throw new Error(
          "Le backend a répondu, mais le format de la liste des collaborateurs est inattendu."
        );
      }

      // Utiliser les mêmes données que le rapport annuel, qui calcule correctement N-1 + N.
      const reportResponse = await apiClient.get("/leave-reports/annual", {
        params: { year: new Date().getFullYear() },
      });
      const reportData = reportResponse.data;
      const reportRows = Array.isArray(reportData)
        ? reportData
        : reportData?.results ?? reportData?.employees ?? reportData?.data?.results ?? [];
      const totals = {};
      (Array.isArray(reportRows) ? reportRows : []).forEach((row) => {
        if (row.employee_id != null) totals[String(row.employee_id)] = reportAnnualTotal(row);
      });
      setAnnualTotalsByEmployee(totals);

      console.table(
        employeeList.map((emp) => ({
          id: emp.id,
          nom: `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
          solde_total: emp.leave_balance?.annual_balance,
          solde_n1: emp.leave_balance?.annual_balance_n1,
          solde_n: emp.leave_balance?.annual_balance_n,
          recuperation: emp.leave_balance?.recovery_balance,
        }))
      );

      setEmployees(employeeList);

      const initialDraft = {};
      employeeList.forEach((emp) => {
        const balanceId = emp.leave_balance?.id;
        if (balanceId != null) {
          initialDraft[balanceId] = "";
        }
      });
      setRestDraft(initialDraft);
    } catch (err) {
      console.error(
        "[EmployeesManagement] Erreur de chargement :",
        err
      );
      setError(
        getErrorMessage(err) ||
          err.message ||
          "Erreur lors du chargement des collaborateurs."
      );
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };


useEffect(() => {
  let cancelled = false;

  const loadEmployees = async () => {
    setError("");
    setLoading(true);

    try {
      // 1. Charger tous les collaborateurs
      const list = await employeeService.getAll();

      const employeeList = Array.isArray(list)
        ? list
        : Array.isArray(list?.items)
        ? list.items
        : Array.isArray(list?.employees)
        ? list.employees
        : null;

      if (!employeeList) {
        throw new Error(
          "Format de réponse invalide pour GET /employees."
        );
      }

      // 2. Charger les soldes calculés par le backend
      const reportResponse = await apiClient.get(
        "/leave-reports/annual",
        {
          params: {
            year: new Date().getFullYear(),
          },
        }
      );

      // 3. Normaliser les différents formats de réponse possibles
      const reportData = reportResponse.data;

      const reportRows = Array.isArray(reportData)
        ? reportData
        : Array.isArray(reportData?.results)
        ? reportData.results
        : Array.isArray(reportData?.employees)
        ? reportData.employees
        : Array.isArray(reportData?.data)
        ? reportData.data
        : Array.isArray(reportData?.data?.results)
        ? reportData.data.results
        : [];

      // 4. Construire les soldes par identifiant employé
      const annualTotals = {};
      const recoveryTotals = {};

      reportRows.forEach((row) => {
        if (row.employee_id == null) return;

        const employeeId = String(row.employee_id);

        // Solde annuel restant : N-1 + N
        annualTotals[employeeId] = reportAnnualTotal(row);

        // Solde de récupération restant calculé par le backend
        const recoveryRemaining = getRecoveryRemaining(row);

        if (recoveryRemaining !== null) {
          recoveryTotals[employeeId] =
            Math.round(recoveryRemaining * 100) / 100;
        }
      });

      if (cancelled) return;

      // 5. Actualiser les états React
      setAnnualTotalsByEmployee(annualTotals);
      setRecoveryTotalsByEmployee(recoveryTotals);
      setEmployees(employeeList);

      // 6. Initialiser les champs d'ajout de récupération
      const initialDraft = {};

      employeeList.forEach((emp) => {
        const balanceId = emp.leave_balance?.id;

        if (balanceId != null) {
          initialDraft[balanceId] = "";
        }
      });

      setRestDraft(initialDraft);

      console.log(
        "[EmployeesManagement] Collaborateurs chargés :",
        employeeList.length
      );

      console.log(
        "[EmployeesManagement] Soldes annuels chargés :",
        annualTotals
      );

      console.log(
        "[EmployeesManagement] Soldes de récupération chargés :",
        recoveryTotals
      );
    } catch (err) {
      if (cancelled) return;

      console.error(
        "[EmployeesManagement] Erreur de chargement :",
        err
      );

      setError(
        getErrorMessage(err) ||
          err.message ||
          "Impossible de charger les collaborateurs."
      );

      setEmployees([]);
      setAnnualTotalsByEmployee({});
      setRecoveryTotalsByEmployee({});
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  };

  loadEmployees();

  return () => {
    cancelled = true;
  };
}, []);


  const startEdit = (emp) => {
    navigate(`/rh/employees/${emp.id}/edit`);
  };

  const handleDelete = async (emp) => {
    if (!window.confirm(`Voulez-vous vraiment désactiver/supprimer ${emp.first_name} ${emp.last_name} ?`)) {
      return;
    }

    try {
      await employeeService.delete(emp.id);
      toast.success("Collaborateur supprimé.");
      await load();
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err));
    }
  };

const handleSaveRestBalance = async (balanceId) => {
  try {
    const addedDays = parseFloat(restDraft[balanceId]);

    if (!Number.isFinite(addedDays) || addedDays <= 0) {
      toast.error("Saisissez un nombre de jours à ajouter supérieur à 0.");
      return;
    }

    // Retrouver le solde actuel du collaborateur
    const employee = employees.find(
      (emp) => Number(emp.leave_balance?.id) === Number(balanceId)
    );

    if (!employee || !employee.leave_balance) {
      toast.error("Solde de récupération introuvable.");
      return;
    }

    const currentBalance = Number(
      employee.leave_balance.recovery_balance ?? 0
    );

    // Ajouter les nouveaux jours au solde disponible.
    // Les jours déjà utilisés ont déjà été déduits
    // lors de la validation du congé.
    const newBalance = Math.round(
      (currentBalance + addedDays) * 100
    ) / 100;

    await leaveService.updateLeaveBalance(balanceId, newBalance);

    toast.success(
      `Solde mis à jour : ${currentBalance} + ${addedDays} = ${newBalance} jours.`
    );

    await load();
  } catch (err) {
    console.error("Erreur de mise à jour du solde :", err);
    toast.error(getErrorMessage(err));
  }
};

  // Filtered employees
  const filteredEmployees = employees.filter((emp) => {
    const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
    const email = (emp.email || "").toLowerCase();
    const dept = (emp.department || "").toLowerCase();
    const q = search.toLowerCase();

    if (q && !fullName.includes(q) && !email.includes(q) && !dept.includes(q)) {
      return false;
    }

    if (departmentFilter !== "ALL" && emp.department !== departmentFilter) {
      return false;
    }

    if (roleFilter !== "ALL" && (emp.role || "").toUpperCase() !== roleFilter) {
      return false;
    }

    if (statusFilter !== "ALL") {
      const isActive = emp.is_active !== false;
      if (statusFilter === "ACTIVE" && !isActive) return false;
      if (statusFilter === "INACTIVE" && isActive) return false;
    }

    return true;
  });

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <PageHeader
        title="Gestion des Collaborateurs"
        subtitle="Gérez l'ensemble de l'effectif, les rôles, départements et soldes de congés."
        actions={
          <button
            onClick={() => navigate("/rh/employees/new")}
            className="btn btn-primary"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Plus size={18} />
            <span>Nouveau collaborateur</span>
          </button>
        }
      />

          {/* Filter Bar */}
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            placeholder="Rechercher par nom, email, département..."
            filters={[
              {
                id: "department",
                label: "Département",
                value: departmentFilter,
                onChange: setDepartmentFilter,
                options: [
                  { value: "ALL", label: "Tous les départements" },
                  ...departmentsList.map(d => ({ value: d, label: d }))
                ]
              },
              {
                id: "role",
                label: "Rôle",
                value: roleFilter,
                onChange: setRoleFilter,
                options: [
                  { value: "ALL", label: "Tous les rôles" },
                  { value: "EMPLOYEE", label: "Employé" },
                  { value: "MANAGER", label: "Manager" },
                  { value: "RH", label: "RH" }
                ]
              },
              {
                id: "status",
                label: "Statut",
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { value: "ALL", label: "Tous les statuts" },
                  { value: "ACTIVE", label: "Actif" },
                  { value: "INACTIVE", label: "Inactif" }
                ]
              }
            ]}
            onReset={() => {
              setSearch("");
              setDepartmentFilter("ALL");
              setRoleFilter("ALL");
              setStatusFilter("ALL");
            }}
          />

          {/* Employees Table */}
          <div className="ui-card" style={{ overflow: "hidden" }}>
            {loading ? (
              <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
                Chargement de la liste des collaborateurs...
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
                Aucun collaborateur ne correspond à vos filtres.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                  <thead>
                    <tr style={{ backgroundColor: "var(--bg-subtle)", borderBottom: "1px solid var(--border)", color: "var(--text-muted)", fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Collaborateur</th>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Poste / Dept</th>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Rôle</th>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Manager</th>
                      <th style={{ padding: "12px 16px", textAlign: "center" }}>Solde Annuel</th>
                      <th style={{ padding: "12px 16px", textAlign: "center" }}>Récupération</th>
                      <th style={{ padding: "12px 16px", textAlign: "center" }}>Statut</th>
                      <th style={{ padding: "12px 16px", textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((emp) => {
                      const isActive = emp.is_active !== false;
                      const initial = emp.first_name ? emp.first_name.charAt(0).toUpperCase() : "U";

                      return (
                        <tr key={emp.id} style={{ borderBottom: "1px solid var(--border)", transition: "var(--transition-fast)" }}>
                          {/* Name & Avatar */}
                          <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <div style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                backgroundColor: "var(--primary-light)",
                                color: "var(--primary)",
                                fontWeight: "700",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.95rem"
                              }}>
                                {initial}
                              </div>
                              <div>
                                <div style={{ fontWeight: "600", color: "var(--text-main)" }}>
                                  {emp.first_name} {emp.last_name}
                                </div>
                                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                                  {emp.email}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Job title & dept */}
                          <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                            <div style={{ fontWeight: "500", color: "var(--text-main)" }}>
                              {emp.job_title || "Collaborateur"}
                            </div>
                            <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                              {emp.department || "Ingénierie"}
                            </div>
                          </td>

                          {/* Role */}
                          <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                            <span style={{
                              padding: "4px 10px",
                              borderRadius: "var(--radius-pill)",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              backgroundColor: emp.role === "RH" ? "var(--status-approved-bg)" : emp.role === "MANAGER" ? "var(--status-pending-bg)" : "var(--status-info-bg)",
                              color: emp.role === "RH" ? "var(--status-approved-text)" : emp.role === "MANAGER" ? "var(--status-pending-text)" : "var(--status-info-text)"
                            }}>
                              {emp.role || "EMPLOYEE"}
                            </span>
                          </td>

                          {/* Manager */}
                          <td style={{ padding: "14px 16px", verticalAlign: "middle", color: "var(--text-muted)", fontSize: "0.88rem" }}>
                            {emp.manager_name || "-"}
                          </td>
{/* Solde annuel restant = solde N-1 restant + solde N restant */}
<td
  style={{
    padding: "14px 16px",
    textAlign: "center",
    verticalAlign: "middle",
    fontWeight: 700,
    color: "var(--primary)",
  }}
>
  {annualTotalsByEmployee[String(emp.id)] !== undefined
    ? `${annualTotalsByEmployee[String(emp.id)].toLocaleString("fr-FR", {
        maximumFractionDigits: 2,
      })}j`
    : "-"}
</td>
                          {/* Solde actuel et ajout de jours de récupération */}
                          <td
                            style={{
                              padding: "14px 16px",
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            {emp.leave_balance ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: "6px",
                                  minWidth: "100px",
                                }}
                              >
                                {/* Solde disponible actuel */}
                                <strong
                                  style={{
                                    color: "var(--primary)",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                {(
                                  recoveryTotalsByEmployee[String(emp.id)] ??
                                  Number(emp.leave_balance.recovery_balance ?? 0)
                                ).toLocaleString("fr-FR", {
                                  maximumFractionDigits: 2,
                                })}{" "}j
                                </strong>

                                {/* Nombre de nouveaux jours à ajouter */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "5px",
                                  }}
                                >
                                  <input
                                    type="number"
                                    min="0.25"
                                    step="0.25"
                                    placeholder=""
                                    aria-label={`Jours de récupération à ajouter pour ${emp.first_name} ${emp.last_name}`}
                                    value={restDraft[emp.leave_balance.id] ?? ""}
                                    onChange={(e) =>
                                      setRestDraft((previous) => ({
                                        ...previous,
                                        [emp.leave_balance.id]: e.target.value,
                                      }))
                                    }
                                    style={{
                                      width: "78px",
                                      maxWidth: "100%",
                                      padding: "6px",
                                      borderRadius: "var(--radius-sm)",
                                      border: "1px solid var(--border)",
                                      textAlign: "center",
                                      fontSize: "0.85rem",
                                      boxSizing: "border-box",
                                    }}
                                  />

                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleSaveRestBalance(emp.leave_balance.id)
                                    }
                                    className="btn btn-small btn-outline"
                                    style={{
                                      padding: "6px 8px",
                                      fontSize: "0.75rem",
                                      flexShrink: 0,
                                    }}
                                  >
                                   +
                                  </button>
                                </div>
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>

                          {/* Status */}
                          <td style={{ padding: "14px 16px", textAlign: "center", verticalAlign: "middle" }}>
                            <span style={{
                              padding: "4px 8px",
                              borderRadius: "var(--radius-pill)",
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              backgroundColor: isActive ? "var(--status-approved-bg)" : "var(--status-rejected-bg)",
                              color: isActive ? "var(--status-approved-text)" : "var(--status-rejected-text)"
                            }}>
                              {isActive ? "Actif" : "Inactif"}
                            </span>
                          </td>

                          {/* Actions */}
                          <td style={{ padding: "14px 16px", textAlign: "right", verticalAlign: "middle" }}>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                              <button
                                onClick={() => startEdit(emp)}
                                className="btn btn-icon"
                                title="Modifier le collaborateur"
                                style={{ padding: "6px", color: "var(--text-muted)" }}
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                onClick={() => handleDelete(emp)}
                                className="btn btn-icon"
                                title="Désactiver / Supprimer"
                                style={{ padding: "6px", color: "var(--status-rejected-text)" }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
    </div>
  );
};

export default EmployeesManagement;

